import React, { useEffect, useState } from 'react'
import injectStyles from 'react-jss'
import { withStyles } from '@material-ui/core/styles'
import CircularProgress from '@material-ui/core/CircularProgress'

import { authorization } from '../../../Module/Auth/Auth'
import {
  getPitchQuestions,
  setPitchQuestions as setPitchQuestionsGraphQL,
} from '../../../Module/graphQL'

import PitchQuestionsFormView from '../component/PitchQuestionsFormView'
import PitchQuestionItem from '../blocks/PitchQuestionItem/container/PitchQuestionItem'

import { PITCH_QUESTION_TYPES } from '../../../shared/constants'

import styles from '../styles'
import { checkIfShouldDisableSaveButton } from './util'
import SubmitButton from 'Components/Form/SubmitButton'

const ColorCircularProgress = withStyles({
  root: {
    color: 'white',
  },
})(CircularProgress)

const questionTemplate = {
  id: 'questionTemplate',
  choices: [''],
  correctAnswerIndex: 0,
  description: '',
  type: PITCH_QUESTION_TYPES.MULTIPLE_CHOICE,
}

const PitchQuestionsForm = ({ campaignId, classes }) => {
  const [uploading, setUploading] = useState(false)
  const [pitchQuestions, setPitchQuestions] = useState([])
  const [pitchQuestionsData, setPitchQuestionsData] = useState({})
  const [canSave, setCanSave] = useState(true)

  useEffect(() => {
    fetchPitchQuestions({ campaignId })
  }, [campaignId])

  useEffect(() => {
    const pitchQuestionsDataArr = Object.values(pitchQuestionsData)

    const disableSaveButton = () => {
      setCanSave(checkIfShouldDisableSaveButton(pitchQuestionsDataArr))
    }

    if (!pitchQuestionsDataArr || !pitchQuestionsDataArr.length) {
      return setPitchQuestions([])
    }

    const pitchQuestions = pitchQuestionsDataArr.map(pitchQuestionData => {
      const {
        id,
        choices,
        correctAnswerIndex,
        description,
        type,
      } = pitchQuestionData
      return (
        <PitchQuestionItem
          key={id}
          questionId={id}
          choices={choices}
          correctAnswerIndex={correctAnswerIndex}
          description={description}
          type={type}
          onAddOption={onAddOption}
          onDeleteOption={onDeleteOption}
          onDeleteQuestion={onDeleteQuestion}
          onSetCorrectAnswer={onSetCorrectAnswer}
          onOptionChange={onOptionChange}
          onQuestionChange={onQuestionChange}
        />
      )
    })
    disableSaveButton()
    setPitchQuestions(pitchQuestions)
  }, [pitchQuestionsData])

  const fetchPitchQuestions = async ({ campaignId }) => {
    const authToken = authorization.getAPIKey()
    const receivedPitchQuestions = await getPitchQuestions({
      authToken,
      campaignId,
    })
    const {
      data: { pitchQuestions },
    } = await receivedPitchQuestions.json()

    if (pitchQuestions && pitchQuestions.length < 1) return

    const formattedPitchQuestions = pitchQuestions.reduce(
      (acc, pitchQuestion) => {
        acc[pitchQuestion.id] = {
          ...pitchQuestion,
          correctAnswerIndex: pitchQuestion.choices.indexOf(
            pitchQuestion.correctAnswer,
          ),
        }
        return acc
      },
      {},
    )

    setPitchQuestionsData(formattedPitchQuestions)
  }

  const submitQuestions = async () => {
    setUploading(true)
    const authToken = authorization.getAPIKey()

    const pitchQuestionsDataPayload = Object.values(pitchQuestionsData).map(
      pitchQuestionData => {
        return {
          id: pitchQuestionData.id,
          description: pitchQuestionData.description,
          type: pitchQuestionData.type,
          choices: pitchQuestionData.choices,
          correctAnswer:
            pitchQuestionData.choices[pitchQuestionData.correctAnswerIndex],
        }
      },
    )

    await setPitchQuestionsGraphQL({
      authToken,
      campaignId,
      pitchQuestionsData: pitchQuestionsDataPayload,
    })
    setUploading(false)
    fetchPitchQuestions({ campaignId })
  }

  const onAddOption = async ({ questionId }) => {
    await setPitchQuestionsData(pitchQuestionsData => ({
      ...pitchQuestionsData,
      [questionId]: {
        ...pitchQuestionsData[questionId],
        choices: [...pitchQuestionsData[questionId].choices, ''],
      },
    }))
  }

  const onOptionChange = async ({ questionId, optionIndex, text }) => {
    await setPitchQuestionsData(pitchQuestionsData => {
      const choices = [...pitchQuestionsData[questionId].choices]
      choices[optionIndex] = text
      return {
        ...pitchQuestionsData,
        [questionId]: {
          ...pitchQuestionsData[questionId],
          choices,
        },
      }
    })
  }

  const onSetCorrectAnswer = ({ questionId, correctAnswerIndex }) => {
    if (
      correctAnswerIndex === pitchQuestionsData[questionId].correctAnswerIndex
    ) {
      return setPitchQuestionsData(pitchQuestionsData => ({
        ...pitchQuestionsData,
        [questionId]: {
          ...pitchQuestionsData[questionId],
          correctAnswerIndex: null,
        },
      }))
    }

    setPitchQuestionsData(pitchQuestionsData => ({
      ...pitchQuestionsData,
      [questionId]: {
        ...pitchQuestionsData[questionId],
        correctAnswerIndex,
      },
    }))
  }

  const onDeleteOption = ({ questionId, optionIndex }) => {
    setPitchQuestionsData(pitchQuestionsData => {
      const choices = [...pitchQuestionsData[questionId].choices]
      choices.splice(optionIndex, 1)
      return {
        ...pitchQuestionsData,
        [questionId]: {
          ...pitchQuestionsData[questionId],
          choices,
        },
      }
    })
  }

  const onDeleteQuestion = ({ questionId }) => {
    setPitchQuestionsData(pitchQuestionsData => {
      const pitchQuestionsDataProcessed = Object.keys(
        pitchQuestionsData,
      ).reduce((acc, id) => {
        if (id != questionId) {
          acc[id] = pitchQuestionsData[id]
        }
        return acc
      }, {})
      return pitchQuestionsDataProcessed
    })
  }

  const onAddQuestion = () => {
    setPitchQuestionsData(pitchQuestionsData => ({
      ...pitchQuestionsData,
      [`newQuestion-${pitchQuestions.length}`]: {
        ...questionTemplate,
        id: `newQuestion-${pitchQuestions.length}`,
      },
    }))
  }

  const onQuestionChange = async ({ questionId, value }) => {
    await setPitchQuestionsData(pitchQuestionsData => ({
      ...pitchQuestionsData,
      [questionId]: {
        ...pitchQuestionsData[questionId],
        description: value,
      },
    }))
  }

  return (
    <PitchQuestionsFormView>
      {pitchQuestions}
      <footer className={classes.footer}>
        <SubmitButton
          variant="contained"
          onClick={onAddQuestion}
          className={classes.button}
          value="Add Question"
        />
        <SubmitButton
          variant="contained"
          onClick={submitQuestions}
          className={classes.button}
          disabled={canSave}
          value={uploading ? <ColorCircularProgress size={25} /> : 'Save'}
        />
      </footer>
    </PitchQuestionsFormView>
  )
}

export default injectStyles(styles)(PitchQuestionsForm)



// WEBPACK FOOTER //
// ./src/Components/PitchQuestionsForm/container/PitchQuestionsForm.js